/**
 * What native's Marvin cards ask the engine to do, through the web's own API clients (one implementation;
 * Swift has no HTTP code for these): decide an approval (`RunApi.decideAction`), read the user's draft for a
 * markup card (`MarkupApi.getVisible`), and keep or remove Marvin's markups (`MarkupApi.decideMarvinShapes`).
 * `conversationEngine.ts` routes each command here and reports the answer with `reportCommandResult`.
 *
 * Every card for a sheet reads its draft when it shows and after every decision, so the cards for one draft
 * address share one read while it is in flight, and no answer reports an older save of a draft than one the
 * engine has already seen (`isOlderDraft`, the web card's rule): a slow read never undoes a decision.
 */
import { MarkupApi, type ApiMarkupSet, type MarvinShapesDecision } from "../api/markup";
import { RunApi } from "../api/run";
import { getPlatform } from "../platform/platformRef";
import {
    draftStamp,
    isOlderDraft,
    ownDraft,
    toDecisionShapes,
    type DecisionShapes,
    type DraftAddress,
    rowStates,
    type MarkupRowState,
} from "../shared/marvinMarkupDecisions";

/** A markup card's draft address, and for a decision the shapes and the decision. */
export interface EngineMarkupCommand {
    fileId: string;
    page: number;
    /** The revision the markups were saved under; absent on a card from before it was recorded. */
    revisionId?: string | null;
    shapeIds?: string[];
    decision?: MarvinShapesDecision;
    /** The card's rows, by shape id: the answer says where each stands (`rowStates`). */
    rowIds?: string[];
}

/** An approval's outcome: the final status on a clean answer, or already decided (the stream says how). */
export interface EngineDecisionResult {
    status: "executed" | "failed" | "rejected" | "expired" | null;
    alreadyDecided: boolean;
}

/** Each of Marvin's shapes in the user's draft and whether it is kept; null when the draft is not readable there. */
export interface EngineMarkupState {
    shapes: DecisionShapes | null;
    /** Each of `rowIds`' state: open, kept, removed, or unknown (the draft was not readable there). */
    rows?: MarkupRowState[];
}

/** A decision's answer: the draft's shapes as the card reads them, and the saved draft for an open viewer. */
export interface EngineMarkupDecision extends EngineMarkupState {
    set: ApiMarkupSet;
}

export function isMarkupCommand(value: unknown): value is EngineMarkupCommand {
    if (typeof value !== "object" || value === null) return false;
    const markup = value as Record<string, unknown>;
    return (
        typeof markup.fileId === "string" &&
        markup.fileId.length > 0 &&
        typeof markup.page === "number" &&
        Number.isInteger(markup.page) &&
        markup.page >= 0
    );
}

function addressOf(markup: EngineMarkupCommand): DraftAddress {
    return { fileId: markup.fileId, page: markup.page, revisionId: markup.revisionId ?? null };
}

/** A draft address in this tenant: entity ids are not unique across tenants. */
function keyOf({ fileId, page, revisionId }: DraftAddress): string {
    return `${getPlatform().tenant.orgSlug() ?? ""}:${fileId}:${page}:${revisionId ?? ""}`;
}

/** The read of each address in flight, which every card asking meanwhile shares. */
const readsInFlight = new Map<string, Promise<ApiMarkupSet | null>>();
/** The newest saved draft seen at each address, for as many addresses as a conversation's cards name. */
const newestDrafts = new Map<string, ApiMarkupSet>();
const NEWEST_DRAFTS_KEPT = 64;

/** `draft`, unless the engine has seen a newer save of it, which it answers instead; remembers the newest. */
function newest(key: string, draft: ApiMarkupSet | null): ApiMarkupSet | null {
    const held = newestDrafts.get(key);
    if (held && isOlderDraft(draftStamp(held), draft)) return held;
    if (draft) {
        newestDrafts.delete(key);
        newestDrafts.set(key, draft);
        if (newestDrafts.size > NEWEST_DRAFTS_KEPT) {
            newestDrafts.delete(newestDrafts.keys().next().value as string);
        }
    }
    return draft;
}

function readOwnDraft(key: string, address: DraftAddress): Promise<ApiMarkupSet | null> {
    const pending = readsInFlight.get(key);
    if (pending) return pending;
    const read = MarkupApi.getVisible(address.fileId, address.page, address.revisionId, null)
        .then((sets) => ownDraft(sets))
        .finally(() => {
            if (readsInFlight.get(key) === read) readsInFlight.delete(key);
        });
    readsInFlight.set(key, read);
    return read;
}

/** A draft read at `address`, as the card reads it: its shapes and, for the card's rows, each row's state. */
function stateOf(
    draft: Parameters<typeof toDecisionShapes>[0],
    address: DraftAddress,
    rowIds: string[] | undefined,
): EngineMarkupState {
    const state = toDecisionShapes(draft, address);
    const shapes = Array.isArray(state) ? state : null;
    if (!rowIds) return { shapes };
    return {
        shapes,
        rows: rowStates(
            rowIds.map((shapeId) => ({ shapeId })),
            shapes ?? "unknown",
        ),
    };
}

export async function decideAction(
    runId: string,
    actionId: string,
    approve: boolean,
): Promise<EngineDecisionResult> {
    const { status, alreadyDecided } = await RunApi.decideAction(runId, actionId, approve);
    return { status, alreadyDecided };
}

export async function readMarvinShapes(markup: EngineMarkupCommand): Promise<EngineMarkupState> {
    const address = addressOf(markup);
    const key = keyOf(address);
    return stateOf(newest(key, await readOwnDraft(key, address)), address, markup.rowIds);
}

export async function decideMarvinShapes(
    markup: EngineMarkupCommand,
    shapeIds: string[],
    decision: MarvinShapesDecision,
): Promise<EngineMarkupDecision> {
    const address = addressOf(markup);
    // The same call the web card makes for a sheet that is not open (`decideMarvinMarkups`).
    const set = address.revisionId
        ? await MarkupApi.decideMarvinShapes(
              address.fileId,
              address.page,
              shapeIds,
              decision,
              address.revisionId,
          )
        : await MarkupApi.decideMarvinShapes(address.fileId, address.page, shapeIds, decision);
    const key = keyOf(address);
    // A read that began before this decision answers its own card; the cards that ask now read afresh.
    readsInFlight.delete(key);
    // Another card's later decision may have answered first: report the newest save, here and to the viewer.
    const saved = newest(key, set) ?? set;
    return { ...stateOf(saved, address, markup.rowIds), set: saved };
}
